import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useIntl } from "react-intl";

import useAuth from "../../hooks/useAuth";
import useLastEquipments from "../../hooks/useLastEquipments";
import useLastUsers from "../../hooks/useLastUsers";
import { displayDate } from "../../utils/date";
import EquipmentIcon from "../../components/icons/Equipment";
import UsersIcon from "../../components/icons/Users";
import PageSectionHeader from "../../components/PageSectionHeader";
import tereosLogo from "/tereos_white.png";

export default function HomeAdmin () {
  const intl = useIntl();
  const { user } = useAuth();

  const { lastEquipments, fetchLastEquipments, isLoading: equipmentsLoading } = useLastEquipments();
  const { lastUsers, fetchLastUsers, isLoading: usersLoading } = useLastUsers();

  useEffect(() => {
    fetchLastEquipments();
    fetchLastUsers();
  }, []);

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage(
            { id: "homeAdmin.documentTitle" },
            { appName: import.meta.env.VITE_APP_NAME }
          )}
        </title>
      </Helmet>
      <div className="h-full flex flex-col">
        <PageSectionHeader className="mb-2">
          <h2 className="text-2xl font-semibold" style={{ color: '#023865' }}>
            {intl.formatMessage(
              { id: "homeAdmin.welcome" },
              { firstName: user?.firstName, lastName: user?.lastName }
            )}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {intl.formatMessage({ id: "homeAdmin.subtitle" })}
          </p>
        </PageSectionHeader>
        
        <section className="mt-8 bg-white rounded-2xl p-6" style={{ border: '1px solid #E4E7EC' }}>
          <div className="mb-6">
            <EquipmentIcon className="w-6 h-6 text-gray-700 mb-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              {intl.formatMessage({ id: "homeAdmin.lastEquipments" })}
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            {equipmentsLoading ? (
              <p className="text-sm text-gray-400 py-4">
                {intl.formatMessage({ id: "common.loading" })}
              </p>
            ) : !lastEquipments || lastEquipments.length === 0 ? (
              <p className="text-sm text-gray-400 py-4">
                {intl.formatMessage({ id: "homeAdmin.noEquipment" })}
              </p>
            ) : (
              <table className="w-full border-separate border-spacing-y-3">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-normal text-gray-600" style={{ width: '15%' }}>
                      {intl.formatMessage({ id: "homeAdmin.colId" })}
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-normal text-gray-600" style={{ width: '35%' }}>
                      {intl.formatMessage({ id: "homeAdmin.colDesignation" })}
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-normal text-gray-600" style={{ width: '35%' }}>
                      {intl.formatMessage({ id: "homeAdmin.colLocation" })}
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-normal text-gray-600" style={{ width: '15%' }}>
                      {intl.formatMessage({ id: "homeAdmin.colStatus" })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lastEquipments.map((equipment) => (
                    <tr key={equipment.id}>
                      <td className="px-4 py-4 text-left text-sm font-medium text-gray-900 rounded-l-2xl" style={{ border: '1px solid #E4E7EC', borderRight: 'none' }}>
                        {equipment.ConnectedObject?.url ?? `#${equipment.id}`}
                      </td>
                      <td className="px-4 py-4 text-left text-sm font-medium text-orange-500" style={{ borderTop: '1px solid #E4E7EC', borderBottom: '1px solid #E4E7EC' }}>
                        {equipment.designation ?? intl.formatMessage({ id: "common.dash" })}
                      </td>
                      <td className="px-4 py-4 text-left text-sm text-gray-700" style={{ borderTop: '1px solid #E4E7EC', borderBottom: '1px solid #E4E7EC' }}>
                        {equipment.location ?? intl.formatMessage({ id: "common.dash" })}
                      </td>
                      <td className="px-4 py-4 text-left text-sm rounded-r-2xl" style={{ border: '1px solid #E4E7EC', borderLeft: 'none' }}>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          equipment.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {equipment.status === 'active'
                            ? intl.formatMessage({ id: "common.active" })
                            : intl.formatMessage({ id: "common.inactive" })}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <div className="relative mt-8">
          <section className="bg-white rounded-2xl p-6" style={{ border: '1px solid #E4E7EC' }}>
            <div className="mb-6">
              <UsersIcon className="w-6 h-6 text-gray-700 mb-2" />
              <h3 className="text-lg font-semibold text-gray-900">
                {intl.formatMessage({ id: "homeAdmin.lastUsers" })}
              </h3>
            </div>
            
            <div className="overflow-x-auto">
              {usersLoading ? (
                <p className="text-sm text-gray-400 py-4">
                  {intl.formatMessage({ id: "common.loading" })}
                </p>
              ) : !lastUsers || lastUsers.length === 0 ? (
                <p className="text-sm text-gray-400 py-4">
                  {intl.formatMessage({ id: "homeAdmin.noUsers" })}
                </p>
              ) : (
                <table className="w-full border-separate border-spacing-y-3">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-normal text-gray-600" style={{ width: '30%' }}>
                        {intl.formatMessage({ id: "homeAdmin.colName" })}
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-normal text-gray-600" style={{ width: '40%' }}>
                        {intl.formatMessage({ id: "homeAdmin.colEmail" })}
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-normal text-gray-600" style={{ width: '20%' }}>
                        {intl.formatMessage({ id: "homeAdmin.colDate" })}
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-normal text-gray-600" style={{ width: '10%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lastUsers.map((u) => (
                      <tr key={u.id}>
                        <td className="px-4 py-4 text-left text-sm font-medium text-gray-900 rounded-l-2xl" style={{ border: '1px solid #E4E7EC', borderRight: 'none' }}>
                          {u.lastName} {u.firstName}
                        </td>
                        <td className="px-4 py-4 text-left text-sm font-medium text-orange-500" style={{ borderTop: '1px solid #E4E7EC', borderBottom: '1px solid #E4E7EC' }}>
                          {u.email}
                        </td>
                        <td className="px-4 py-4 text-left text-sm" style={{ borderTop: '1px solid #E4E7EC', borderBottom: '1px solid #E4E7EC' }}>
                          <span className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium" style={{ backgroundColor: '#F9FAFB', color: '#9CA3AF' }}>
                            {displayDate(u.createdAt)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-left text-sm rounded-r-2xl" style={{ border: '1px solid #E4E7EC', borderLeft: 'none' }}>
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-gray-400">
                            <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </div>      

        <img
          src={tereosLogo}
          alt={intl.formatMessage({ id: "common.tereosAlt" })}
          className="block ml-auto mt-2"
          style={{ width: 736, height: 203 }}
        />  
      </div>
    </>
  )
}
